/**
 * Utility functions for JWT token handling
 */

interface JwtPayload {
    exp?: number; // Expiration time (seconds since epoch)
    iat?: number; // Issued at time
    [key: string]: any;
}

/**
 * Decode a JWT token without verification
 * @param token - The JWT token string
 * @returns The decoded payload or null if invalid
 */
export function decodeJwt(token: string): JwtPayload | null {
    try {
        // JWT structure: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        // Decode the payload (second part)
        let payload = parts[1];
        
        // Normalize base64url to base64 and add padding if needed
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        payload += '='.repeat((4 - payload.length % 4) % 4);
        
        const decodedPayload = atob(payload);
        return JSON.parse(decodedPayload);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
}

/**
 * Get the expiration time of a JWT token
 * @param token - The JWT token string
 * @returns The expiration time in milliseconds, or null if not available
 */
export function getTokenExpiration(token: string): number | null {
    const payload = decodeJwt(token);
    if (!payload || !payload.exp) {
        return null;
    }
    // Convert from seconds to milliseconds
    return payload.exp * 1000;
}

/**
 * Check if a JWT token is expired
 * @param token - The JWT token string
 * @returns true if expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
    const expiration = getTokenExpiration(token);
    if (!expiration) {
        return true; // Consider invalid tokens as expired
    }
    return Date.now() >= expiration;
}

/**
 * Get the time remaining until token expiration in milliseconds
 * @param token - The JWT token string
 * @returns Time remaining in milliseconds, or 0 if expired/invalid
 */
export function getTimeUntilExpiration(token: string): number {
    const expiration = getTokenExpiration(token);
    if (!expiration) {
        return 0;
    }
    const remaining = expiration - Date.now();
    return Math.max(0, remaining);
}
